import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "styled-components";
import { getToneTokens } from "../../../styles/shared";
import {
  ETAPA,
  ETAPA_LABELS,
  ORDEM_ETAPAS,
} from "../../../utils/toc/etapas";
import {
  distribuicaoEtapaNoPeriodo,
  expandirLogGargalo,
} from "../../../utils/toc/bottleneckHistory";
import { EmptyHint, SectionH2, SectionHeader } from "./styles";

const ETAPA_TONES = {
  [ETAPA.COMPRAS]: "info",
  [ETAPA.MAQUINAS]: "purple",
  [ETAPA.TERCEIROS]: "orange",
  [ETAPA.MARCENARIA]: "danger",
  [ETAPA.ACABAMENTO]: "warning",
  [ETAPA.EXPEDICAO]: "lime",
};

const ETAPA_INDICE = Object.fromEntries(
  ORDEM_ETAPAS.map((etapa, idx) => [etapa, idx + 1])
);

const ChartHeight = 260;

export default function BottleneckHistoryChart({ log, hoje, dias = 60 }) {
  const theme = useTheme();
  const diasExpandidos = useMemo(
    () => expandirLogGargalo(log, { hoje, dias }),
    [log, hoje, dias]
  );
  const { counts, semDado } = useMemo(
    () => distribuicaoEtapaNoPeriodo(diasExpandidos),
    [diasExpandidos]
  );

  const data = diasExpandidos.map((dia) => ({
    label: dia.label,
    indice: dia.etapa ? ETAPA_INDICE[dia.etapa] : 0,
    etapa: dia.etapa,
  }));

  const algumDado = data.some((d) => d.etapa);

  return (
    <div>
      <SectionHeader>
        <SectionH2>Histórico de gargalos · últimos {dias} dias</SectionH2>
      </SectionHeader>
      {algumDado ? (
        <div style={{ width: "100%", height: ChartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 8, right: 12, bottom: 24, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: theme.textSecondary, fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: theme.border }}
                interval={Math.max(1, Math.floor(data.length / 12))}
              />
              <YAxis
                domain={[0, ORDEM_ETAPAS.length]}
                tickCount={ORDEM_ETAPAS.length + 1}
                tickFormatter={(value) => {
                  const etapa = ORDEM_ETAPAS[value - 1];
                  return etapa ? ETAPA_LABELS[etapa] : "";
                }}
                tick={{ fill: theme.textSecondary, fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: theme.border }}
                width={90}
              />
              <Tooltip
                contentStyle={{
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  fontSize: 12,
                  borderRadius: 8,
                }}
                formatter={(_value, _name, payload) => [
                  payload?.payload?.etapa
                    ? ETAPA_LABELS[payload.payload.etapa]
                    : "Sem dado",
                  "Gargalo",
                ]}
                labelFormatter={(label) => `Dia ${label}`}
              />
              <Bar dataKey="indice" isAnimationActive={false} radius={[4, 4, 0, 0]}>
                {data.map((entry, index) => (
                  <Cell
                    key={`bnh-${index}`}
                    fill={
                      entry.etapa
                        ? getToneTokens(theme, ETAPA_TONES[entry.etapa] || "default").text
                        : theme.surfaceAlt
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyHint>
          Sem histórico de gargalos persistido ainda. Ele começa a ser populado a
          partir desta sessão, conforme o gargalo muda.
        </EmptyHint>
      )}
      {algumDado && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12, fontSize: 12 }}>
          {ORDEM_ETAPAS.map((etapa) => (
            <span key={etapa} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: getToneTokens(theme, ETAPA_TONES[etapa] || "default").text,
                }}
              />
              {ETAPA_LABELS[etapa]} · {counts[etapa] || 0}d
            </span>
          ))}
          {semDado > 0 && (
            <span style={{ color: theme.textSecondary }}>
              Sem dado: {semDado}d
            </span>
          )}
        </div>
      )}
    </div>
  );
}
